import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, CheckCircle2, AlertTriangle, Loader2, ScanLine } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function TicketScanner({ onClose, onCheckedIn }) {
  const [result, setResult] = useState(null); // {ok, attendee, event_title, already_checked_in}
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);
  const lastRef = useRef("");

  const submit = async (token) => {
    if (busy || token === lastRef.current) return;
    lastRef.current = token;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/pos/checkin/scan", { token });
      setResult(data);
      onCheckedIn?.();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Ticket not recognized");
    } finally {
      setBusy(false);
      setTimeout(() => { lastRef.current = ""; }, 2500);
    }
  };

  useEffect(() => {
    const el = "ticket-scan-region";
    const scanner = new Html5Qrcode(el, { verbose: false });
    scannerRef.current = scanner;
    scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 },
      (decoded) => submit(decoded), () => {})
      .catch(() => setError("Couldn't open the camera. Allow camera access, or type the ticket ID below."));
    return () => { try { scanner.stop().then(() => scanner.clear()).catch(() => {}); } catch { /* noop */ } };
  }, []); // eslint-disable-line

  return (
    <div className="fixed inset-0 z-[130] grid place-items-center px-4 bg-black/60 backdrop-blur-sm" data-testid="ticket-scanner">
      <div className="relative w-full max-w-md bg-[#F9F7F3] rounded-3xl shadow-2xl p-6">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#4A5D6B]" data-testid="scanner-close"><X size={20} /></button>
        <h3 className="font-serif text-2xl text-[#1A242B] mb-1 flex items-center gap-2"><ScanLine size={20} className="text-[#144F75]" /> Scan ticket</h3>
        <p className="text-sm text-[#4A5D6B] mb-4">Point the camera at an attendee's ticket QR code.</p>

        <div id="ticket-scan-region" className="rounded-2xl overflow-hidden bg-black aspect-square mb-4" />

        {busy && <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Loader2 className="animate-spin" size={15} /> Checking…</p>}
        {result && (
          <div className={`rounded-xl p-4 ${result.already_checked_in ? "bg-[#E0A458]/15" : "bg-[#5C8A72]/15"}`} data-testid="scan-result">
            <p className="font-medium text-[#1A242B] flex items-center gap-2"><CheckCircle2 size={17} className="text-[#5C8A72]" /> {result.already_checked_in ? "Already checked in" : "Checked in!"}</p>
            <p className="text-sm text-[#4A5D6B] mt-1">{result.attendee} — {result.event_title}</p>
          </div>
        )}
        {error && <div className="rounded-xl p-3 bg-red-50 text-red-700 text-sm flex items-center gap-2" data-testid="scan-error"><AlertTriangle size={15} /> {error}</div>}

        <div className="mt-4 flex gap-2">
          <input placeholder="Or paste ticket ID / URL" data-testid="scan-manual-input"
            className="eyw-input flex-1 text-sm" onKeyDown={(e) => { if (e.key === "Enter") submit(e.target.value.trim()); }} />
        </div>
      </div>
    </div>
  );
}
